import { Injectable } from '@angular/core';
import { of, ReplaySubject, Observable } from 'rxjs';
import { OrganizationDto, OrganizationGroupDto } from '@scip/api';
import { OrganizationControllerService } from '@scip/api';
import { BackendErrorService } from './backend-error.service';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { NotificationService } from './notification.service';
import { OrganizationGroupService } from './organization-group.service';
import { translationKeys } from '../core';

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  orgData: OrganizationDto;
  orgDataChanged: ReplaySubject<OrganizationDto> = new ReplaySubject<OrganizationDto>(1);

  constructor(
    private organizationControllerService: OrganizationControllerService,
    private organizationGroupService: OrganizationGroupService,
    private backendErrorService: BackendErrorService,
    private notificationService: NotificationService
  ) {}

  setOrgData(orgData: OrganizationDto): void {
    this.orgData = orgData;
    this.orgDataChanged.next(this.orgData);
  }

  saveOrganization(): Observable<boolean> {
    // When there is no identifier we know the group is not there and needs to be created before updating/saving the org
    if (this.orgData && this.orgData.organizationGroup && this.orgData.organizationGroup.name && !this.orgData.organizationGroup.identifier) {
      return this.organizationGroupService.createOrganizationGroup(this.orgData.organizationGroup.name).pipe(
        switchMap((newGroup: OrganizationGroupDto) => {
          this.orgData.organizationGroup = newGroup;
          this.orgDataChanged.next(this.orgData); // We could create the group and updated the orgData, so lets emit that
          return this.createNewOrUpdateExisting();
        }),
        catchError(() => {
          return of(false);
        })
      );
    } else {
      return this.createNewOrUpdateExisting();
    }
  }

  private createNewOrUpdateExisting(): Observable<boolean> {
    if (this.orgData.identifier) {
      return this.sendChanges();
    } else {
      return this.createNewOrg();
    }
  }

  private sendChanges(): Observable<boolean> {
    return this.organizationControllerService.updateOrganization(this.orgData).pipe(
      tap(() => this.notificationService.showSuccess('profileService.msg.success.updatingOrganization')),
      map(() => true),
      catchError((error) => {
        this.backendErrorService.handleError(error, translationKeys.profileService.msg.error.updatingOrganization);
        return of(false);
      })
    );
  }

  private createNewOrg(): Observable<boolean> {
    return this.organizationControllerService.addOrganization(this.orgData).pipe(
      tap(() => this.notificationService.showSuccess('profileService.msg.success.addingOrganization')),
      map(() => true),
      catchError((error) => {
        this.backendErrorService.handleError(error, translationKeys.profileService.msg.error.addingOrganization);
        return of(false);
      })
    );
  }

  getAllOrganizations(
    onlyActiveOnes: boolean,
    onlyPrimaryOnes: boolean = false,
    sortedByName: boolean = false,
    includeS2SKey: boolean = false
  ): Observable<OrganizationDto[]> {
    return this.organizationControllerService.getAllOganizations(onlyActiveOnes, includeS2SKey).pipe(
      map((allOrgs: OrganizationDto[]) => {
        if (onlyPrimaryOnes) {
          return allOrgs.filter((singleOrg) => singleOrg.primary);
        } else {
          return allOrgs;
        }
      }),
      map((allOrgs: OrganizationDto[]) => {
        if (sortedByName) {
          return allOrgs.sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
        } else {
          return allOrgs;
        }
      }),
      catchError((error) => {
        this.backendErrorService.handleError(error);
        return of([]);
      })
    );
  }

  deleteOrganization(identifier: string): Observable<boolean> {
    return this.organizationControllerService.deleteOrganization(identifier).pipe(
      tap(() => this.notificationService.showSuccess('profileService.msg.success.deletingOrganization')),
      map(() => true),
      catchError((error) => {
        this.backendErrorService.handleError(error, translationKeys.profileService.msg.error.deletingOrganization);
        return of(false);
      })
    );
  }
}
